// =============================================================================
// DASHBOARD OVERVIEW - TYPE DEFINITIONS
// =============================================================================

export interface KpiMetrics {
  totalCalls: {
    total: number
    inbound: number
    outbound: number
  }
  bookings: {
    total: number
    conversionRate: number
    opportunities: number
    booked: number
  }
  avgQualityScore: {
    newOpps: number
    existing: number
    insurance: number
  }
  missedCalls: {
    total: number
    changePercent: number
  }
}

export interface TopPerformer {
  rank: number
  name: string
  calls: number
  bookings: number
  score: number
}

export type CallResult = 'booked' | 'existing' | 'lost_opp' | 'inquiry' | 'not_booked' | 'missed' | 'insurance' | 'not_relevant' | 'business' | 'outbound'
export type CallDirection = 'inbound' | 'outbound'
export type LeadType = 'new' | 'existing'
export type BrandColor = 'blue' | 'green' | 'orange'

export interface CallRecord {
  id: number
  time: string
  direction: CallDirection
  fromName?: string | null
  fromPhone?: string | null
  fromCompany?: string | null
  toPhone?: string | null
  toCompany?: string | null
  csrName: string
  durationSec?: number
  result: CallResult
  leadType: LeadType | null
  isNewOpportunity: boolean
  score: number
  hasRecording: boolean
  summary: string
  telephonySessionId?: string | null
  brand: string
  brandColor?: BrandColor
  ringTime?: string | null
  subCategory?: string | null
  notes?: string | null
  scoringBreakdown?: Record<string, number> | null
  route?: string | null
}

export interface MissedCallRecord {
  id: string
  date: string
  time: string
  fromName: string
  fromPhone: string | null
  durationSec: number
  hasVoicemail: boolean
  telephonySessionId: string
  wasCalledBack: boolean
  callbackMinutes: number | null
  callbackOperator?: string | null
  wasSecondAttempt: boolean
  secondAttemptMinutes: number | null
  isRepeatCaller?: boolean // Same phone number called multiple times (show yellow)
}

export interface FilterOptions {
  period: 'today' | 'yesterday' | 'week' | 'month' | 'custom'
  result: CallResult | 'all'
  csr: string | 'any'
  scoreRange: 'all' | 'analyzed' | 'high' | 'medium' | 'low'
  search: string
}

export interface PaginationInfo {
  currentPage: number
  totalPages: number
  totalItems: number
  itemsPerPage: number
}

export interface DashboardData {
  kpiMetrics: KpiMetrics
  topPerformers: TopPerformer[]
  calls: CallRecord[]
  missedCalls: MissedCallRecord[]
  pagination: PaginationInfo
  filters: FilterOptions
}

// Result badge colors mapping
export const RESULT_STYLES: Record<CallResult, { bg: string; text: string; label: string }> = {
  booked: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Booked' },
  existing: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Existing' },
  lost_opp: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Lost Opp.' },
  inquiry: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Inquiry' },
  not_booked: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Not Booked' },
  missed: { bg: 'bg-red-100', text: 'text-red-700', label: 'Missed' },
  insurance: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Insurance' },
  not_relevant: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Not Relevant' },
  business: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Business' },
  outbound: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Outbound' },
}

// Score thresholds for color coding
export const SCORE_THRESHOLDS = {
  EXCELLENT: 80,  // 80+ green
  GOOD: 70,       // 70-79 yellow
  FAIR: 61,       // 61-69 orange
  // ≤60 red
}

export function getScoreColor(score: number): string {
  if (score >= SCORE_THRESHOLDS.EXCELLENT) return 'bg-emerald-100 text-emerald-700'
  if (score >= SCORE_THRESHOLDS.GOOD) return 'bg-yellow-100 text-yellow-700'
  if (score >= SCORE_THRESHOLDS.FAIR) return 'bg-orange-100 text-orange-700'
  return 'bg-red-100 text-red-600'
}

export function getConvRateColor(rate: number): string {
  if (rate >= 80) return 'text-emerald-600 bg-emerald-50'
  if (rate >= 70) return 'text-blue-600 bg-blue-50'
  if (rate >= 60) return 'text-yellow-600 bg-yellow-50'
  return 'text-red-600 bg-red-50'
}

// Brand colors mapping
export const BRAND_COLORS: Record<BrandColor, string> = {
  blue: 'bg-blue-500',
  green: 'bg-emerald-500',
  orange: 'bg-orange-500',
}
